<script setup lang="ts">
/**
 * Persistent warning shown while the session MQTT client is down (FLY3D-1029).
 *
 * This exists because the failure it reports used to be silent. The dashboard
 * abandoned MQTT after three failed auth attempts and raised one transient
 * toast, leaving the operator logged in, looking at stale telemetry, and
 * unreachable by takeover requests without knowing it. Reconnection is now
 * indefinite, and while it is unresolved the operator is told plainly.
 *
 * Deliberately not dismissible: the consequence (someone can take your dock
 * believing you have left) persists whether or not the operator has
 * acknowledged the message.
 */
import { storeToRefs } from 'pinia'
import { mqttStore } from '@/store/mqtt'

const { realtimeDegraded, sessionState } = storeToRefs(mqttStore())
</script>

<template>
  <div v-if="realtimeDegraded" class="realtime-banner" role="alert" aria-live="assertive">
    <span class="realtime-banner__dot" aria-hidden="true" />
    <div class="realtime-banner__text">
      <strong>Real-time connection lost.</strong>
      You will not receive takeover requests, and colleagues may see you as offline.
      <span v-if="sessionState === 'reconnecting'">Reconnecting…</span>
    </div>
  </div>
</template>

<style scoped>
.realtime-banner {
  position: fixed;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10000;
  display: flex;
  align-items: center;
  gap: 0.625rem;
  max-width: 720px;
  margin: 0.5rem;
  padding: 0.625rem 1rem;
  border: 1px solid rgba(226, 87, 76, 0.6);
  border-radius: 10px;
  background: var(--idi-shade0-85, rgba(11, 14, 18, 0.95));
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(10px);
  color: var(--idi-white, #fff);
  font-size: 0.8125rem;
  line-height: 1.35;
}

.realtime-banner__dot {
  flex: 0 0 auto;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: #e2574c;
  animation: realtime-pulse 1.6s ease-in-out infinite;
}

.realtime-banner__text {
  min-width: 0;
}

@keyframes realtime-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.35;
  }
}

@media (prefers-reduced-motion: reduce) {
  .realtime-banner__dot {
    animation: none;
  }
}
</style>
